/**
 * PR-D4-062 — Session review card using the universal Bluebook split layout.
 */

import { useEffect, useState } from 'react';
import type { Question } from '../lib/types';
import { KaTeXRenderer } from './KaTeXRenderer';
import { NativeGraphRenderer } from './NativeGraphRenderer';
import { sanitizeExplanationText } from './MathQuestionCard';
import { QuestionVoteBar } from './QuestionVoteBar';
import {
    extractExplanation,
    hasAnyExplanation,
    hasBilingualExplanation,
    resolveExplanationSource,
} from './QuestionCard';
import UniversalSplitLayout, {
    PassageContent,
    getQuestionSvgFallback,
    questionHasPassage,
    questionHeaderLabel,
    resolveStimulusKind,
} from './UniversalSplitLayout';

const REVIEW_DIAGRAM_HEIGHT_PX = 460;

const OPTION_LABELS = ['A', 'B', 'C', 'D'] as const;

export interface SessionReviewCardProps {
    question: Question;
    questionNumber: number;
    totalQuestions: number;
    selectedOption: number | null;
    isCorrect: boolean;
    timeSpentSeconds?: number;
}

export function SessionReviewCard({
    question,
    questionNumber,
    totalQuestions,
    selectedOption,
    isCorrect,
    timeSpentSeconds,
}: SessionReviewCardProps) {
    const explanationSource = resolveExplanationSource(question);
    const bilingual = hasBilingualExplanation(explanationSource);
    const anyExplanation = hasAnyExplanation(explanationSource);
    const [explanationLang, setExplanationLang] = useState<'en' | 'ru'>('en');

    useEffect(() => {
        const src = resolveExplanationSource(question);
        setExplanationLang(hasBilingualExplanation(src) ? 'ru' : 'en');
    }, [question.id]);

    const correctIndex = question.correct_option_index;
    const correctLetter = OPTION_LABELS[correctIndex] ?? 'A';
    const rawExplanation = extractExplanation(explanationSource, explanationLang);
    const explanationText = sanitizeExplanationText(rawExplanation, correctLetter);

    const svgFallback = getQuestionSvgFallback(question);
    const stimulusKind = resolveStimulusKind(question);
    const passage = (question.passage ?? '').trim();

    const stimulus =
        stimulusKind === 'diagram' ? (
            <>
                <div className="universal-split__diagram-frame">
                    <NativeGraphRenderer
                        graphData={question.graph_data}
                        svgFallback={svgFallback}
                        height={REVIEW_DIAGRAM_HEIGHT_PX}
                        width="100%"
                        className="session-review-card__diagram-canvas"
                    />
                </div>
                {questionHasPassage(question) ? <PassageContent text={passage} /> : null}
            </>
        ) : stimulusKind === 'passage' ? (
            <PassageContent text={passage} />
        ) : null;

    return (
        <article className="session-review-card">
            <UniversalSplitLayout
                mode="review"
                stimulusKind={stimulusKind}
                header={
                    <>
                        <h2 className="universal-split__header-title mt-0">
                            {questionHeaderLabel(questionNumber, totalQuestions)}
                        </h2>
                        <span
                            className={`session-review-card__badge ${
                                isCorrect
                                    ? 'session-review-card__badge--correct'
                                    : 'session-review-card__badge--incorrect'
                            }`}
                        >
                            {isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                        {timeSpentSeconds != null ? (
                            <span className="session-review-card__meta">{timeSpentSeconds}s</span>
                        ) : null}
                    </>
                }
                stimulus={stimulus}
            >
                <div className="question-text mt-0">
                    <KaTeXRenderer text={question.question_text} />
                </div>

                <div className="answer-interaction-box w-full">
                    <ul className="mcq-options-list" aria-label="Answer choices">
                        {(question.options ?? []).map((text, idx) => {
                            const letter = OPTION_LABELS[idx] ?? String(idx);
                            const isUserChoice = selectedOption === idx;
                            const isCorrectChoice = idx === correctIndex;
                            const statusClass = isCorrectChoice
                                ? 'correct-choice'
                                : isUserChoice
                                  ? 'incorrect-choice'
                                  : '';
                            return (
                                <li
                                    key={`${question.id}-${letter}`}
                                    className={`mcq-option-item session-review-card__choice ${statusClass}`}
                                >
                                    <span className="choice-letter">{letter}</span>
                                    <div className="choice-text">
                                        <KaTeXRenderer text={text} />
                                    </div>
                                    {isCorrectChoice ? (
                                        <span className="session-review-card__choice-tag">Correct</span>
                                    ) : null}
                                    {isUserChoice && !isCorrectChoice ? (
                                        <span className="session-review-card__choice-tag session-review-card__choice-tag--yours">
                                            Your answer
                                        </span>
                                    ) : null}
                                    {isUserChoice && isCorrectChoice ? (
                                        <span className="session-review-card__choice-tag">Your answer</span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {selectedOption == null ? (
                    <p className="session-review-card__skipped">No answer selected for this question.</p>
                ) : null}

                <section className="sat-feedback-box w-full" aria-label="Explanation">
                    <div
                        className="sat-feedback-title"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                        }}
                    >
                        <span>Solution</span>
                        {bilingual ? (
                            <span className="session-review-card__lang" aria-label="Explanation language">
                                [{' '}
                                <button
                                    type="button"
                                    className={explanationLang === 'en' ? 'is-active' : ''}
                                    onClick={() => setExplanationLang('en')}
                                >
                                    EN
                                </button>
                                {' | '}
                                <button
                                    type="button"
                                    className={explanationLang === 'ru' ? 'is-active' : ''}
                                    onClick={() => setExplanationLang('ru')}
                                >
                                    RU
                                </button>
                                {' ]'}
                            </span>
                        ) : null}
                    </div>
                    <div className="sat-feedback-text">
                        {anyExplanation && explanationText ? (
                            <div className="math-text math-text--prose">
                                <KaTeXRenderer text={explanationText} />
                            </div>
                        ) : (
                            <p className="session-review-card__missing">
                                Explanation is unavailable for this question.
                            </p>
                        )}
                    </div>
                    <QuestionVoteBar questionId={question.id} />
                </section>
            </UniversalSplitLayout>
        </article>
    );
}

export default SessionReviewCard;
